/** Ставка склада ₽/шт без НДС: на сегодня в шапке + окно «Изменить» (новая ставка с даты, история). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "sonner";

import { addWarehouseRate } from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dateRu, dateTimeRu, money, parseMoney, rateOn, todayIso } from "./format";
import { invalidatePrices, useRates } from "./queries";
import { ErrorBox, Field, Modal } from "./ui";

export function WarehouseRateBar(): React.ReactElement {
  const q = useRates();
  const [open, setOpen] = useState(false);
  const today = todayIso();
  const cur = q.data ? rateOn(q.data, today) : null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="warehouse-rate">
      <span className="text-muted-foreground">Ставка склада на сегодня:</span>
      {q.isLoading && <span className="text-muted-foreground">загрузка…</span>}
      {q.isError && <ErrorBox error={q.error} what="Ставка склада не загрузилась" />}
      {q.data &&
        (cur ? (
          <span className="font-medium tabular-nums">
            {money(cur.rub_per_unit)}/шт без НДС
            <span className="ml-1 font-normal text-muted-foreground">с {dateRu(cur.valid_from)}</span>
          </span>
        ) : (
          <span className="font-medium text-red-600 dark:text-red-400">не задана на сегодня</span>
        ))}
      <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(true)} disabled={!q.data}>
        Изменить
      </Button>
      {open && q.data && <RateDialog onClose={() => setOpen(false)} />}
    </div>
  );
}

function RateDialog({ onClose }: { onClose: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const q = useRates();
  const [validFrom, setValidFrom] = useState(todayIso());
  const [rub, setRub] = useState("");
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  const parsed = parseMoney(rub, { positive: false });
  const m = useMutation({
    mutationFn: (body: Parameters<typeof addWarehouseRate>[0]) => addWarehouseRate(body),
    onSuccess: async (r) => {
      toast.success(`Ставка ${money(r.rub_per_unit)}/шт с ${dateRu(r.valid_from)} сохранена`);
      await invalidatePrices(qc);
      onClose();
    },
  });
  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTried(true);
    if (!parsed.ok || !validFrom) return;
    m.mutate({ valid_from: validFrom, rub_per_unit: parsed.value, note: note.trim() || null });
  };
  return (
    <Modal title="Ставка склада, ₽/шт без НДС" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Действует с" error={tried && !validFrom ? "укажите дату" : null}>
            {(id) => (
              <Input id={id} type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            )}
          </Field>
          <Field label="Ставка, ₽/шт" error={tried && !parsed.ok ? parsed.error : null}>
            {(id) => (
              <Input id={id} inputMode="decimal" value={rub} onChange={(e) => setRub(e.target.value)} />
            )}
          </Field>
        </div>
        <Field label="Комментарий (необязательно)">
          {(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        {m.isError && <ErrorBox error={m.error} what="Ставка не сохранена" />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={m.isPending}>
            Сохранить ставку
          </Button>
        </div>
      </form>
      <h4 className="mb-1 mt-4 text-sm font-semibold">История ставок</h4>
      {q.isError && <ErrorBox error={q.error} what="История ставок не загрузилась" />}
      {q.data && q.data.length === 0 && <p className="text-sm text-muted-foreground">Ставок ещё нет.</p>}
      {q.data && q.data.length > 0 && (
        <table className="w-full text-sm" data-testid="rate-history">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-2 font-medium">С даты</th>
              <th className="py-1 pr-2 text-right font-medium">₽/шт</th>
              <th className="py-1 pr-2 font-medium">Комментарий</th>
              <th className="py-1 font-medium">Кто, когда</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="py-1 pr-2 tabular-nums">{dateRu(r.valid_from)}</td>
                <td className="py-1 pr-2 text-right tabular-nums">{money(r.rub_per_unit)}</td>
                <td className="py-1 pr-2">{r.note ?? ""}</td>
                <td className="py-1 text-xs text-muted-foreground">
                  {r.created_by}, {dateTimeRu(r.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
